import React, { Component, useEffect, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  Link,
  NavLink,
  useNavigate,
} from "react-router-dom";
import { Outlet } from "react-router";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";

import {
  CalendarPlus,
  LogOut,
  Menu,
  Settings as SettingsIcon,
  X,
} from "lucide-react";
import LeaveModal from "./components/LeaveModal.tsx";
import { Button } from "./components/ui/button.tsx";
import { Checkbox } from "./components/ui/checkbox.tsx";
import BrandLogo from "./components/BrandLogo.tsx";

import AuthService from "./services/auth.service";

import Login from "./pages/Login.tsx";
import Home from "./pages/Home.tsx";
import AppointmentsList from "./pages/AppointmentsList.tsx";
import AddAppointments from "./pages/AddAppointments.tsx";
import ClientConfirmation from "./pages/ClientConfirmation.tsx";
import CancelAppointment from "./pages/CancelAppointment.tsx";
import ServiceCalendar from "./pages/ServiceCalendar.tsx";
import ServiceList from "./pages/ServiceList.tsx";
import Settings from "./pages/Settings.tsx";
import ServiceConfirmation from "./pages/ServiceConfirmation.tsx";
import ServiceCancel from "./pages/ServiceCancel.tsx";
import UserService from "./services/user.service";
import type { CurrentUser } from "./pages/models.ts";

type NavBarProps = {
  currentUser?: CurrentUser;
  logOut: () => void;
};

type AppState = {
  currentUser: CurrentUser | undefined;
};

const NavBar = ({ currentUser, logOut }: NavBarProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLeaves, setShowLeaves] = useState(() => {
    const saved = localStorage.getItem("showLeaves");
    return saved === null ? true : saved === "true";
  });
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const syncShowLeaves = () => {
      setShowLeaves(localStorage.getItem("showLeaves") !== "false");
    };

    window.addEventListener("showLeavesChanged", syncShowLeaves);
    return () =>
      window.removeEventListener("showLeavesChanged", syncShowLeaves);
  }, []);

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    handleMobileMenuClose();
    logOut();

    navigate("/login");
  };

  const handleShowLeavesChange = (checked: boolean) => {
    localStorage.setItem("showLeaves", String(checked));
    setShowLeaves(checked);
    window.dispatchEvent(new Event("showLeavesChanged"));
  };

  const handleLeaveChanged = () => {
    window.dispatchEvent(new Event("leavesUpdated"));
  };

  const companyName = currentUser?.company_name || currentUser?.companyName;
  const companyLogoUrl = currentUser?.logo_url || currentUser?.logoUrl;

  return (
    <>
      <nav className="border-b border-slate-200 bg-white shadow-sm">
        <div className="mx-auto flex min-h-16 max-w-screen-2xl items-center gap-3 px-4 sm:px-6">
          <div className="flex shrink-0 items-center gap-3">
            <Link
              to="/"
              className="flex shrink-0 items-center gap-2.5 text-decoration-none"
              aria-label="Motomeet home"
            >
              <BrandLogo
                alt={companyName || "Motomeet"}
                src={companyLogoUrl}
                variant="icon"
                className="h-10 w-auto sm:h-11"
              />
              {companyName && (
                <div className="hidden flex-col sm:flex text-left">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 leading-none">
                    motoscheduler for
                  </span>
                  <span className="text-sm font-bold text-slate-800 leading-tight">
                    {companyName}
                  </span>
                </div>
              )}
            </Link>
          </div>
          {currentUser ? (
            <>
              <div className="hidden items-center gap-1 md:flex">
                {currentUser.dyno_enabled !== false && (
                  <NavLink
                    to="/dyno"
                    className={({ isActive }) =>
                      `rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                        isActive
                          ? "bg-sky-100 text-sky-800"
                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                      }`
                    }
                  >
                    Dyno
                  </NavLink>
                )}
                <NavLink
                  to="/service"
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                      isActive
                        ? "bg-sky-100 text-sky-800"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                    }`
                  }
                >
                  Service
                </NavLink>
              </div>
              <div className="ml-auto hidden items-center gap-2 md:flex">
                <label className="flex h-9 cursor-pointer items-center gap-2 rounded-md px-2 text-sm font-medium text-slate-600 hover:bg-slate-100">
                  <Checkbox
                    checked={showLeaves}
                    onCheckedChange={(checked) =>
                      handleShowLeavesChange(checked === true)
                    }
                  />
                  Technical leaves
                </label>
                <Button
                  variant="outline"
                  onClick={() => setShowLeaveModal(true)}
                >
                  <CalendarPlus className="h-4 w-4" />
                  Add leave
                </Button>
                <Link to="/settings" aria-label="Settings" title="Settings">
                  <Button variant="ghost" size="icon">
                    <SettingsIcon className="h-4 w-4" />
                  </Button>
                </Link>
                <Button variant="ghost" onClick={handleLogout}>
                  <LogOut className="h-4 w-4" />
                  Log out
                </Button>
              </div>
              <div className="ml-auto md:hidden">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                  onClick={() => setIsMobileMenuOpen((open) => !open)}
                >
                  {isMobileMenuOpen ? (
                    <X className="h-5 w-5" />
                  ) : (
                    <Menu className="h-5 w-5" />
                  )}
                </Button>
              </div>
            </>
          ) : (
            <div className="ml-auto">
              <Link to="/login">
                <Button variant="outline">Log in</Button>
              </Link>
            </div>
          )}
        </div>
        {currentUser && isMobileMenuOpen && (
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 md:hidden">
            <div className="mx-auto flex max-w-screen-2xl flex-col gap-1">
              {companyName && (
                <div className="mb-2 border-b border-slate-200 px-3 pb-2 text-left">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    motoscheduler for
                  </div>
                  <div className="text-sm font-bold text-slate-800">
                    {companyName}
                  </div>
                </div>
              )}
              {currentUser.dyno_enabled !== false && (
                <NavLink
                  to="/dyno"
                  onClick={handleMobileMenuClose}
                  className="rounded-md px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
                >
                  Dyno
                </NavLink>
              )}
              <NavLink
                to="/service"
                onClick={handleMobileMenuClose}
                className="rounded-md px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
              >
                Service
              </NavLink>
              <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md px-3 text-sm font-semibold text-slate-700 hover:bg-slate-200">
                <Checkbox
                  checked={showLeaves}
                  onCheckedChange={(checked) =>
                    handleShowLeavesChange(checked === true)
                  }
                />
                Technical leaves
              </label>
              <Button
                variant="ghost"
                className="justify-start"
                onClick={() => {
                  setShowLeaveModal(true);
                  handleMobileMenuClose();
                }}
              >
                <CalendarPlus className="h-4 w-4" />
                Add leave
              </Button>
              <Link to="/settings" onClick={handleMobileMenuClose}>
                <Button variant="ghost" className="w-full justify-start">
                  <SettingsIcon className="h-4 w-4" />
                  Settings
                </Button>
              </Link>
              <Button
                variant="ghost"
                className="justify-start"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" />
                Log out
              </Button>
            </div>
          </div>
        )}
      </nav>
      <LeaveModal
        show={showLeaveModal}
        onHide={() => setShowLeaveModal(false)}
        onLeaveChanged={handleLeaveChanged}
      />
    </>
  );
};

const OurOutlet = () => {
  return (
    <div className="mx-auto mt-3 max-w-screen-2xl px-4 sm:px-6">
      <Outlet />
    </div>
  );
};

const WithNavbar = ({ currentUser, logOut }: NavBarProps) => {
  return (
    <>
      <NavBar currentUser={currentUser} logOut={logOut} />
      <OurOutlet />
    </>
  );
};

const WithoutNavbar = () => {
  return (
    <>
      <OurOutlet />
    </>
  );
};

class App extends Component<Record<string, never>, AppState> {
  constructor(props: Record<string, never>) {
    super(props);
    this.logOut = this.logOut.bind(this);

    this.state = {
      currentUser: undefined,
    };
  }

  async componentDidMount() {
    const user = AuthService.getCurrentUser();

    if (user) {
      this.setState({
        currentUser: user,
      });

      const companyName = user.company_name || user.companyName;
      if (companyName) {
        document.title = `Motoscheduler for ${companyName}`;
      } else {
        try {
          const res = await UserService.getAccountSettings();
          const fetchedCompanyName =
            res?.data?.data?.company_name || res?.data?.data?.companyName;
          if (fetchedCompanyName) {
            const updatedUser = {
              ...user,
              company_name: fetchedCompanyName,
              companyName: fetchedCompanyName,
            };
            AuthService.setUser(updatedUser);
            this.setState({ currentUser: updatedUser });
            document.title = `Motoscheduler for ${fetchedCompanyName}`;
          } else {
            document.title = "Motoscheduler";
          }
        } catch {
          document.title = "Motoscheduler";
        }
      }
    } else {
      document.title = "Motoscheduler";
    }
  }

  logOut() {
    AuthService.logout();
    this.setState({
      currentUser: undefined,
    });
    document.title = "Motoscheduler";
  }

  render() {
    const { currentUser } = this.state;

    return (
      <div>
        <Routes>
          <Route element={<WithoutNavbar />}>
            <Route path="/login" element={<Login />} />
            <Route path="/confirm/:id" element={<ClientConfirmation />} />
            <Route path="/cancel/:id" element={<CancelAppointment />} />
            <Route
              path="/service/confirm/:id"
              element={<ServiceConfirmation />}
            />
            <Route path="/service/cancel/:id" element={<ServiceCancel />} />
          </Route>

          <Route
            element={
              <WithNavbar currentUser={currentUser} logOut={this.logOut} />
            }
          >
            <Route
              path="/"
              element={
                currentUser ? (
                  <Navigate to="/dyno" replace={true} />
                ) : (
                  <Navigate to="/login" replace={true} />
                )
              }
            />
            <Route
              path="/home"
              element={<Navigate to="/dyno" replace={true} />}
            />
            <Route path="/dyno" element={<Home />} />
            <Route path="/add" element={<AddAppointments />} />
            <Route path="/list" element={<AppointmentsList />} />
            <Route path="/service" element={<ServiceCalendar />} />
            <Route path="/service/list" element={<ServiceList />} />
            <Route path="/settings" element={<Settings />} />
          </Route>
        </Routes>
      </div>
    );
  }
}

export default App;
